import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  Bell,
  Clock,
  Lock,
  MessageSquare,
  ShieldAlert,
  Store,
  User,
  UserCheck,
  X,
} from 'lucide-react';
import { cn } from 'lib/utils';
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from 'components/ui/drawer';
import { Skeleton } from 'components/ui/skeleton';
import EmptyCell from 'components/EmptyCell';
import { initials } from 'components/rating/RatingForm';
import { formatDateTime, formatDayTime } from 'utils/formatDate';
import { formatDuration } from 'utils/elapsed';
import { apiErrorMessage } from 'utils/errorMessage';
import { useMissionRequests } from 'hooks/useInterventionRequests';
import type { FollowUpRequest, InterventionRequest } from 'api/interventionRequestsApi';
import {
  interventionDay,
  knownRefusalReasonLabel,
  pendingSolicitationsLabel,
  technicianName,
} from 'pages/AdminIssues/components/upcomingAssignment';
import { DRAWER_BADGE, DrawerRow, DrawerSection } from './DrawerSection';
import { MissionSolicitations } from './MissionSolicitations';
import {
  followUpAnswerLabel,
  followUpStatusLabel,
  missionKnown,
  specialtyTone,
  statusDot,
  statusTone,
  terminalReason,
} from '../vocabulary';

/**
 * One request, read in full: the intervention, the technician, the message that went out, the
 * answer with its delay and its reason, and the mission's whole chronology.
 *
 * **No action** (D36). Reassigning lives on the ticket screen, where the candidate ranking is; the
 * footer names that screen instead of offering a second way to get there — and the mock-up's
 * « Relancer le technicien » is gone with the `expired` status D5 deleted.
 *
 * ⚠️ `vaul` stamps `touch-action: none` on `DrawerContent`: the scrolling region must be a **child**
 * of it, and the footer stays outside that child or it scrolls away with the content.
 */

const DASH = '—';

/** The fainter of the two muted greys — the mock-up's `--muted-2`, for labels above a value. */
const MICRO_LABEL = 'text-[11px] font-medium uppercase tracking-[.04em] text-muted-foreground/70';

/** The rail needs a sky tint for a send, which no assignment state carries — hence its own map. */
const TIMELINE_TINTS = {
  sky: 'bg-sky-500 ring-sky-50 dark:ring-sky-950',
  emerald: 'bg-emerald-500 ring-emerald-50 dark:ring-emerald-950',
  rose: 'bg-rose-500 ring-rose-50 dark:ring-rose-950',
  amber: 'bg-amber-500 ring-amber-50 dark:ring-amber-950',
  zinc: 'bg-zinc-400 ring-zinc-100 dark:ring-zinc-800',
};

type TimelineTint = keyof typeof TIMELINE_TINTS;

interface TimelineEntry {
  id: string;
  time: string;
  title: string;
  sub: string;
  tint: TimelineTint;
}

/**
 * The chronology as a rail: every request contributes its send, then what came back — or, while it
 * is open, the fact that nothing has. Oldest first, which is the only order a timeline reads in.
 */
const timelineOf = (t: TFunction, chronology: InterventionRequest[]): TimelineEntry[] =>
  [...chronology].reverse().flatMap((request): TimelineEntry[] => {
    const sub = technicianName(request);
    const time = request.respondedAt ? formatDayTime(request.respondedAt) : DASH;
    /*
     * The send is unconditional and the answer is optional, rather than one `switch` returning
     * both: a status the union does not cover would otherwise return `undefined`, and `flatMap`
     * would drop the send with it — the one entry that is true whatever came back.
     */
    const answer = ((): Omit<TimelineEntry, 'id'> | null => {
      switch (request.status) {
        case 'pending':
          // No timestamp to print, and never an invented one: the answer has not happened.
          return { time: DASH, title: t('Awaiting an answer from the technician'), sub, tint: 'amber' };
        case 'accepted':
          return { time, title: t('Request accepted'), sub, tint: 'emerald' };
        case 'declined':
          return { time, title: t('Request declined'), sub, tint: 'rose' };
        case 'cancelled':
          return { time, title: t('Reassigned or released'), sub, tint: 'zinc' };
        case 'superseded':
          return { time, title: t('Filled by another technician'), sub, tint: 'zinc' };
        default:
          return null;
      }
    })();

    const sent: TimelineEntry = {
      id: `${request.id}-sent`,
      time: formatDayTime(request.sentAt),
      title: t('Request sent to the technician'),
      sub,
      tint: 'sky',
    };
    return answer ? [sent, { ...answer, id: `${request.id}-answer` }] : [sent];
  });

interface FollowUpDrawerProps {
  request: FollowUpRequest | null;
  onClose: () => void;
}

export const FollowUpDrawer = ({ request, onClose }: FollowUpDrawerProps) => {
  const { t } = useTranslation();
  // The chronology is its own read, and its own failure: it must not blank the part already on
  // screen, which came with the row and is known.
  const chronology = useMissionRequests(request?.issueKey ?? null, request?.serviceDeskId ?? 0);

  if (!request) return null;

  // The same test the Actions column runs, so the drawer and the row never disagree on whether
  // the ticket can still be opened.
  const known = missionKnown(request);
  // Never a dangling « · » nor a dangling « — »: a missing half drops its separator with it.
  const headline =
    [[request.store, request.storeCity].filter(Boolean).join(' · '), request.requestType]
      .filter(Boolean)
      .join(' — ') || request.issueKey;

  const refusalCode = knownRefusalReasonLabel(t, request.refusalReasonCode);
  const refusalText = request.refusalReason?.trim() || null;
  const answeredOn = request.respondedAt ? formatDateTime(request.respondedAt) : null;
  const hint =
    request.status === 'pending'
      ? t('The technician has not answered the notification yet.')
      : terminalReason(t, request.status);
  // Read off the row, not the chronology: same field and same rule as the Technician column, so
  // the drawer cannot contradict the row that opened it — and no flicker while the chronology loads.
  const namesTechnician =
    pendingSolicitationsLabel(t, request.status, request.liveRequestCount) === null;
  const missionRequests = chronology.data?.requests ?? [];
  const entries = timelineOf(t, missionRequests);

  return (
    <Drawer open direction="right" shouldScaleBackground={false} onOpenChange={(next) => !next && onClose()}>
      {/* One scrolling child: vaul stamps `touch-action: none` on `DrawerContent` itself. */}
      <DrawerContent
        side="right"
        className="w-[560px] max-w-full overflow-hidden shadow-[-16px_0_46px_rgba(0,0,0,.2)]"
        overlayClassName="bg-zinc-950/50 backdrop-blur-[2px]"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b px-[22px] py-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-[10px]">
              <DrawerTitle className="text-[17px] font-semibold leading-tight tracking-normal">
                {t('Request detail')}
              </DrawerTitle>
              {/* The short label here and the long one in « Réponse du technicien »: a 23 px pill
                  holds one word, a sentence belongs where there is room for it. */}
              <span className={cn(DRAWER_BADGE, statusTone(request.status))}>
                <span
                  className={cn('h-1.5 w-1.5 rounded-full', statusDot(request.status))}
                  aria-hidden="true"
                />
                {followUpStatusLabel(t, request.status)}
              </span>
            </div>
            <DrawerDescription className="mt-1.5 text-[13px]">{headline}</DrawerDescription>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('Close')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-[17px] w-[17px]" aria-hidden="true" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto px-[22px] pb-6 pt-5">
          <DrawerSection
            icon={Store}
            title={t('The intervention')}
            aside={
              <span className="whitespace-nowrap font-mono text-[12px] text-muted-foreground">
                {request.issueKey}
              </span>
            }
            bodyClassName="px-4 pb-[14px] pt-1"
          >
            <DrawerRow label={t('Sales outlet')} value={request.store ?? <EmptyCell />} />
            <DrawerRow label={t('City')} value={request.storeCity ?? <EmptyCell />} />
            <DrawerRow label={t('Intervention type')} value={request.requestType ?? <EmptyCell />} />
            <DrawerRow
              label={t('Intervention date')}
              // The day and never a slot: nothing stores one (D11, D15), so the row is named for
              // the date alone rather than promising an hour nobody agreed.
              value={interventionDay(request.interventionDate) ?? <EmptyCell />}
            />
            <DrawerRow
              label={t('Requested skills')}
              value={
                request.missionSpecialties.length > 0 ? (
                  <span className="flex flex-wrap justify-end gap-[5px]">
                    {request.missionSpecialties.map((name) => (
                      <span
                        key={name}
                        className={cn(
                          'inline-flex h-[19px] items-center rounded-[6px] px-[7px] text-[11px] font-semibold',
                          specialtyTone(name).tint
                        )}
                      >
                        {name}
                      </span>
                    ))}
                  </span>
                ) : (
                  <EmptyCell />
                )
              }
            />
            <DrawerRow label={t('Useful information')} value={request.description ?? <EmptyCell />} />
          </DrawerSection>

          {/* The heart of L5b: who else was asked, and who ended up doing it (D47). */}
          <MissionSolicitations
            requests={missionRequests}
            participants={chronology.data?.participants ?? []}
            openedRequestId={request.id}
            isLoading={chronology.isLoading}
            error={chronology.error}
          />

          {/*
            Only when naming somebody is a fact (D53) — the same rule the column runs, fed the same
            way, so the two can never disagree. `request` is the row the server retained, which on
            a mission nobody accepted is simply the most recent one: this block used to show that
            arbitrary person with an avatar and a profile link, two lines under « retenu : personne ».
            `MissionSolicitations` above already lists everyone asked, so nothing is lost by hiding it.
          */}
          {namesTechnician && (
          <DrawerSection
            icon={User}
            title={t('The technician of this request')}
            bodyClassName="flex flex-col gap-[14px] p-4"
          >
            <div className="flex items-center gap-[11px]">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-zinc-100 text-[12.5px] font-semibold text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                aria-hidden="true"
              >
                {initials(technicianName(request))}
              </span>
              <div className="flex min-w-0 flex-col leading-[1.35]">
                <span className="truncate text-[13.5px] font-semibold">
                  {technicianName(request)}
                </span>
                <Link
                  to={`/admin/technicians/${request.technician.id}`}
                  className="w-fit text-[11.5px] text-muted-foreground/70 hover:text-foreground hover:underline"
                >
                  {t('See the technician profile')}
                </Link>
              </div>
            </div>

            {/* Country, departments and specialties are **not** repeated here (D63): the link
                above serves them, up to date, and this drawer tells the story of a request rather
                than of a technician. What stays below belongs to the request and to nothing else. */}
            {request.sentOutsidePerimeter === true && (
              <p className="inline-flex items-start gap-1.5 text-[12.5px] text-amber-700 dark:text-amber-500">
                <ShieldAlert className="mt-[2px] h-[14px] w-[14px] shrink-0" aria-hidden="true" />
                {/* Recorded at the time, never recomputed: a technician's perimeter changes, so
                    today's answer says nothing about yesterday's decision (D22 §3). */}
                {t("This request deliberately went outside the technician's usual area.")}
              </p>
            )}
          </DrawerSection>
          )}

          <DrawerSection
            icon={Bell}
            title={t('Notification sent')}
            aside={
              <span className="whitespace-nowrap font-mono text-[12px] text-muted-foreground">
                {formatDateTime(request.sentAt)}
              </span>
            }
            bodyClassName="flex flex-col gap-3 p-4"
          >
            {/* Read-only: the message left with the send, and rewriting it here would rewrite
                history rather than the notification the technician actually received. */}
            <div className="whitespace-pre-wrap rounded-[10px] border bg-muted/40 px-[14px] py-[13px] text-[13px] leading-[1.6]">
              {request.customMessage ||
                t('The standard notification was sent, with the ticket details.')}
            </div>
            {/*
             * Who validated the send — the question D22 provisioned `sent_by_user_id` for, and it
             * gets asked weeks later. It lives here rather than as a table column: the design's
             * table has no room for it, and « pourquoi celui-là ? » is a question one asks of one
             * request, not of a page of them.
             */}
            {request.sentByUsername && (
              <span className="inline-flex items-start gap-[7px] text-[11.5px] text-muted-foreground">
                <UserCheck className="mt-[2px] h-[13px] w-[13px] shrink-0" aria-hidden="true" />
                {t('Sent by {{name}}', { name: request.sentByUsername })}
              </span>
            )}
            <span className="inline-flex items-start gap-[7px] text-[11.5px] text-muted-foreground/70">
              <Lock className="mt-[2px] h-[13px] w-[13px] shrink-0" aria-hidden="true" />
              {t('Predefined template — generated automatically from the intervention data')}
            </span>
            {/* B-08, open and named rather than silently shipped: the template is translated, the
                Jira content inside it is not. Every technician is `fr` today. */}
            <span className="text-[11.5px] text-muted-foreground/70">
              {t('Ticket content is sent in the language Jira holds it in, not the reader language.')}
            </span>
          </DrawerSection>

          <DrawerSection
            icon={MessageSquare}
            title={t('Technician answer')}
            bodyClassName="flex flex-col gap-3 p-4"
          >
            <div className="flex flex-wrap items-center gap-[10px]">
              <span className={cn(DRAWER_BADGE, statusTone(request.status))}>
                <span
                  className={cn('h-1.5 w-1.5 rounded-full', statusDot(request.status))}
                  aria-hidden="true"
                />
                {followUpAnswerLabel(t, request.status)}
              </span>
              {answeredOn && (
                <span className="text-[12.5px] text-muted-foreground">
                  {/* The delay is Postgres's, never the screen's — and absent rather than zero
                      on the rows recorded before the column existed. */}
                  {request.responseDelayS == null
                    ? t('Answered on {{date}}', { date: answeredOn })
                    : t('Answered on {{date}} · in {{duration}}', {
                        date: answeredOn,
                        duration: formatDuration(request.responseDelayS),
                      })}
                </span>
              )}
              {/*
               * The wait, kept although the mock's pending branch carries only its hint sentence.
               * D5 removed the expiry, so nothing else says a request is rotting — and this
               * drawer is where somebody opens a `pending` row precisely to find out how long.
               */}
              {request.status === 'pending' && (
                <span className="text-[12.5px] font-medium text-amber-700 dark:text-amber-500">
                  {t('Waiting for {{duration}}', {
                    duration: formatDuration(request.elapsedSeconds),
                  })}
                </span>
              )}
            </div>

            {(refusalCode || refusalText) && (
              <div className="flex flex-col gap-[5px] rounded-[10px] border bg-muted/40 px-[14px] py-[13px]">
                <span className="text-[11px] font-medium uppercase tracking-[.05em] text-muted-foreground/70">
                  {t('Refusal reason')}
                </span>
                {/* A code with no free text still says something: the label is the answer. */}
                {refusalCode && <span className="text-[13px] font-medium">{refusalCode}</span>}
                {refusalText && (
                  <span className="text-[13px] italic leading-[1.6]">{`« ${refusalText} »`}</span>
                )}
              </div>
            )}

            {hint && <span className="text-[12.5px] leading-[1.5] text-muted-foreground">{hint}</span>}
          </DrawerSection>

          <DrawerSection icon={Clock} title={t('Request timeline')} bodyClassName="px-4 pb-[6px] pt-4">
            {chronology.isLoading ? (
              <div className="flex flex-col gap-2 pb-3">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-2/3" />
              </div>
            ) : chronology.error ? (
              // Its own failure, and only its own: the four sections above came with the row.
              <p role="alert" className="pb-3 text-[12.5px] text-red-600 dark:text-red-400">
                {apiErrorMessage(t, chronology.error)}
              </p>
            ) : (
              <ol>
                {entries.map((entry, index) => (
                  <li key={entry.id} className="flex gap-[13px] pb-4">
                    <div className="flex w-[14px] shrink-0 flex-col items-center">
                      <span
                        className={cn(
                          'mt-[3px] h-[10px] w-[10px] shrink-0 rounded-full border-2 border-background ring-2',
                          TIMELINE_TINTS[entry.tint]
                        )}
                        aria-hidden="true"
                      />
                      {index < entries.length - 1 && (
                        <span className="mt-1 w-px flex-1 bg-border" aria-hidden="true" />
                      )}
                    </div>
                    <div className="-mt-[3px] flex min-w-0 flex-col gap-[3px]">
                      <span className="font-mono text-[12px] text-muted-foreground/70">
                        {entry.time}
                      </span>
                      <span className="text-[13px] font-medium">{entry.title}</span>
                      {entry.sub && (
                        <span className="text-[12px] text-muted-foreground">{entry.sub}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </DrawerSection>
        </div>

        {/* Outside the scrolling child, or it would scroll away with the content. */}
        <div className="flex shrink-0 items-center justify-end gap-[10px] border-t bg-background px-[22px] py-4">
          {/* Said out loud, because the drawer is the one place a reader might look for a
              button: nothing here writes anything. « Ouvrir le ticket » is a navigation. */}
          <p className="mr-auto text-[12.5px] text-muted-foreground">
            {known
              ? t('No action is possible here.')
              : // Absent with its reason, never a dead link.
                t('This mission is no longer in GPS — the request is kept for the record.')}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-[39px] items-center rounded-[9px] border border-input bg-background px-4 text-[13.5px] font-medium transition-colors hover:bg-accent"
          >
            {t('Close')}
          </button>
          {known && (
            <Link
              to={`/admin/issues/${request.issueKey}`}
              className="inline-flex h-[39px] items-center gap-2 rounded-[9px] bg-zinc-900 px-[17px] text-[13.5px] font-medium text-white transition-colors hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200"
            >
              {/* The one place something can be done about this request (D36). */}
              {t('Open the ticket')}
              <ArrowUpRight className="h-[15px] w-[15px]" aria-hidden="true" />
            </Link>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};
